import React from 'react';
import { Box, Skeleton } from '@mui/material';

/**
 * ChartSkeleton - Skeleton placeholder for Nivo charts
 *
 * Shows a realistic chart skeleton with y-axis labels, chart area, and x-axis labels.
 * Variants support different chart types (bar, line, scatter, treemap, acceleration).
 *
 * @param {string} variant - Chart type: 'bar' | 'line' | 'scatter' | 'treemap' | 'acceleration'
 * @param {number} height - Height of the skeleton in pixels
 * @param {string|number} width - Width of the skeleton (default: '100%')
 * @param {string} barWidth - Width of each bar in bar chart (default: '8%')
 * @param {number} seriesCount - Number of line series for line variant (default: 3)
 */
const ChartSkeleton = ({ variant = 'bar', height = 400, width = '100%', maxWidth, barWidth = '2%', seriesCount = 3, sx = {} }) => {
  const renderYAxisLabels = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', pr: 1, ml:  10,mt:2 }}>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} variant="text" width={40} height={16} />
      ))}
    </Box>
  );

  const renderXAxisLabels = (count = 6) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, pl: 6, mb: 10 }}>
      {[...Array(count)].map((_, i) => (
        <Skeleton key={i} variant="text" width={50} height={16} />
      ))}
    </Box>
  );

  const renderBarChartBody = () => (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: '100%', flex: 1 }}>
      {[.45,.50,.48,.55,.52,.58,.54,.60,.62,.56,.65,.68,.63,.70,.72,.67,.75,.78,.80,.85].map((h, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          width={barWidth}
          height={`${h * 100}%`}
          sx={{ borderRadius: '4px 4px 0 0' }}
        />
      ))}
    </Box>
  );

  // SVG path data for line chart series (uptrend, flat, slight downtrend)
  const lineSeriesPaths = [
    // Series 1: uptrend
    { line: 'M 0 75 C 40 70, 80 55, 120 50 C 160 45, 200 35, 240 30 C 280 25, 320 20, 360 15 C 400 12, 440 10, 480 8', y0: 100 },
    // Series 2: flat with variation
    { line: 'M 0 55 C 40 50, 80 58, 120 52 C 160 48, 200 54, 240 50 C 280 46, 320 52, 360 48 C 400 44, 440 50, 480 46', y0: 100 },
    // Series 3: slight downtrend
    { line: 'M 0 30 C 40 35, 80 32, 120 38 C 160 42, 200 40, 240 48 C 280 52, 320 50, 360 56 C 400 60, 440 58, 480 64', y0: 100 },
  ];

  const seriesColors = ['rgba(128,128,128,0.35)', 'rgba(128,128,128,0.25)', 'rgba(128,128,128,0.20)'];

  const renderLineChartBody = () => {
    const activeSeries = lineSeriesPaths.slice(0, seriesCount);
    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          flex: 1,
          '@keyframes skeletonPulse': {
            '0%': { opacity: 0.6 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.6 },
          },
          animation: 'skeletonPulse 1.5s ease-in-out infinite',
        }}
      >
        <svg
          viewBox="0 0 480 120"
          preserveAspectRatio="none"
          style={{ width: '100%', height: '100%' }}
        >
          {/* Horizontal grid lines at 20/40/60/80% */}
          {[24, 48, 72, 96].map((y, i) => (
            <line key={i} x1="0" y1={y} x2="480" y2={y} stroke="currentColor" strokeOpacity="0.1" strokeWidth="0.5" />
          ))}
          {/* Series area fills + lines */}
          {activeSeries.map((series, i) => {
            // Build area path: line path + close back along bottom
            const areaPath = `${series.line} L 480 ${series.y0} L 0 ${series.y0} Z`;
            return (
              <React.Fragment key={i}>
                <path d={areaPath} fill={seriesColors[i]} fillOpacity="0.06" />
                <path d={series.line} fill="none" stroke={seriesColors[i]} strokeWidth="2" />
              </React.Fragment>
            );
          })}
        </svg>
        {/* Legend placeholders */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '124px', mt: 0.5 }}>
          {activeSeries.map((_, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Skeleton variant="circular" width={12} height={12} />
              <Skeleton variant="text" width={48} height={14} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderScatterChartBody = () => (
    <Box sx={{ position: 'relative', height: '100%', flex: 1 }}>
      {/* Grid lines */}
      {[...Array(4)].map((_, i) => (
        <Skeleton
          key={`h-${i}`}
          variant="rectangular"
          width="100%"
          height={1}
          sx={{ position: 'absolute', top: `${(i + 1) * 20}%`, opacity: 0.3 }}
        />
      ))}
      {/* Scatter points */}
      {[
        { x: '15%', y: '30%' }, { x: '25%', y: '55%' }, { x: '35%', y: '45%' },
        { x: '45%', y: '25%' }, { x: '55%', y: '60%' }, { x: '65%', y: '35%' },
        { x: '75%', y: '50%' }, { x: '85%', y: '40%' }, { x: '20%', y: '70%' },
        { x: '40%', y: '65%' }, { x: '60%', y: '20%' }, { x: '80%', y: '75%' },
      ].map((pos, i) => (
        <Skeleton
          key={i}
          variant="circular"
          width={12}
          height={12}
          sx={{ position: 'absolute', left: pos.x, top: pos.y }}
        />
      ))}
    </Box>
  );

  const renderTreemapBody = () => {
    // Realistic treemap layout with nested rectangles
    // Outer regions represent categories, inner cells represent companies
    const regions = [
      // Large left block (biggest category)
      { x: 0, y: 0, w: 220, h: 100, cells: [
        { x: 1, y: 1, w: 108, h: 58 }, { x: 111, y: 1, w: 108, h: 38 },
        { x: 111, y: 41, w: 60, h: 58 }, { x: 173, y: 41, w: 46, h: 58 },
        { x: 1, y: 61, w: 56, h: 38 }, { x: 59, y: 61, w: 50, h: 38 },
      ]},
      // Top-right block
      { x: 222, y: 0, w: 148, h: 55, cells: [
        { x: 1, y: 1, w: 72, h: 53 }, { x: 75, y: 1, w: 72, h: 30 },
        { x: 75, y: 33, w: 38, h: 21 }, { x: 115, y: 33, w: 32, h: 21 },
      ]},
      // Middle-right block
      { x: 372, y: 0, w: 108, h: 55, cells: [
        { x: 1, y: 1, w: 106, h: 30 }, { x: 1, y: 33, w: 54, h: 21 },
        { x: 57, y: 33, w: 50, h: 21 },
      ]},
      // Bottom-center block
      { x: 222, y: 57, w: 130, h: 43, cells: [
        { x: 1, y: 1, w: 64, h: 41 }, { x: 67, y: 1, w: 62, h: 24 },
        { x: 67, y: 27, w: 62, h: 15 },
      ]},
      // Bottom-right block
      { x: 354, y: 57, w: 126, h: 43, cells: [
        { x: 1, y: 1, w: 68, h: 41 }, { x: 71, y: 1, w: 54, h: 22 },
        { x: 71, y: 25, w: 54, h: 17 },
      ]},
    ];

    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          flex: 1,
          '@keyframes skeletonPulse': {
            '0%': { opacity: 0.6 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.6 },
          },
          animation: 'skeletonPulse 1.5s ease-in-out infinite',
        }}
      >
        <svg
          viewBox="0 0 480 100"
          preserveAspectRatio="none"
          style={{ width: '100%', height: '100%' }}
        >
          {regions.map((region, ri) => (
            <g key={ri} transform={`translate(${region.x}, ${region.y})`}>
              {/* Region border */}
              <rect
                x="0" y="0"
                width={region.w} height={region.h}
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.15"
                strokeWidth="1"
              />
              {/* Inner cells */}
              {region.cells.map((cell, ci) => (
                <rect
                  key={ci}
                  x={cell.x} y={cell.y}
                  width={cell.w} height={cell.h}
                  rx="0.5"
                  fill="currentColor"
                  fillOpacity={0.06 + (ci % 3) * 0.04}
                  stroke="currentColor"
                  strokeOpacity="0.12"
                  strokeWidth="0.5"
                />
              ))}
            </g>
          ))}
        </svg>
      </Box>
    );
  };

  // Vertical sorted bar chart skeleton (like AccelerationBar)
  // Bars extend up (positive/green) or down (negative/red) from a center zero line
  // Sorted left-to-right: most positive to most negative, matching real chart order
  const renderAccelerationBody = () => {
    const spreads = [
      0.40, 0.36, 0.33, 0.30, 0.27, 0.24, 0.21, 0.18, 0.15, 0.12,
      0.09, 0.06, 0.03, 0.01, -0.01, -0.03, -0.05, -0.08, -0.11, -0.14,
      -0.17, -0.20, -0.24, -0.28, -0.32, -0.38,
    ];

    const count = spreads.length;
    const spacing = 480 / count;
    const barW = spacing * 0.65;
    const zeroY = 50; // zero line at vertical midpoint
    const scale = 48; // SVG units per 100% spread

    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          flex: 1,
          '@keyframes skeletonPulse': {
            '0%': { opacity: 0.6 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.6 },
          },
          animation: 'skeletonPulse 1.5s ease-in-out infinite',
        }}
      >
        <svg
          viewBox="0 0 480 100"
          preserveAspectRatio="none"
          style={{ width: '100%', height: '100%' }}
        >
          {/* Horizontal grid lines above and below zero */}
          {[15, 35, 65, 85].map((y, i) => (
            <line key={i} x1="0" y1={y} x2="480" y2={y} stroke="currentColor" strokeOpacity="0.08" strokeWidth="0.5" />
          ))}
          {/* Zero line */}
          <line x1="0" y1={zeroY} x2="480" y2={zeroY} stroke="currentColor" strokeOpacity="0.35" strokeWidth="1" />
          {/* Vertical bars */}
          {spreads.map((s, i) => {
            const cx = spacing * i + spacing / 2;
            const barH = Math.abs(s) * scale;
            const y = s >= 0 ? zeroY - barH : zeroY;
            return (
              <rect
                key={i}
                x={cx - barW / 2}
                y={y}
                width={barW}
                height={Math.max(barH, 0.5)}
                rx="1"
                fill="currentColor"
                fillOpacity={s >= 0 ? 0.20 : 0.12}
                stroke="currentColor"
                strokeOpacity="0.2"
                strokeWidth="0.3"
              />
            );
          })}
        </svg>
      </Box>
    );
  };

  // X-axis for acceleration: many small rotated-style label stubs (simulating ticker labels)
  const renderAccelerationXAxisLabels = () => {
    const count = 26;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-around', pt: 0.5, pl: 6, mb: 10 }}>
        {[...Array(count)].map((_, i) => (
          <Skeleton key={i} variant="text" width={20} height={14} sx={{ transform: 'rotate(-50deg)', transformOrigin: 'top left', mt: 1 }} />
        ))}
      </Box>
    );
  };

  // Waterfall/bridge chart skeleton (like MarginChangeBridge)
  // Bars step up/down showing cumulative margin changes
  const renderWaterfallBody = () => {
    const zeroY = 75;
    const scale = 2;
    const toY = (m) => zeroY - m * scale;

    const bars = [
      { from: 0,    to: 20,   type: 'start' },
      { from: 20,   to: 25,   type: 'positive' },
      { from: 25,   to: 23,   type: 'negative' },
      { from: 23,   to: 20,   type: 'negative' },
      { from: 20,   to: 19,   type: 'negative' },
      { from: 19,   to: 19.5, type: 'positive' },
      { from: 19.5, to: 22,   type: 'positive' },
      { from: 0,    to: 22,   type: 'end' },
    ];

    const count = bars.length;
    const spacing = 480 / count;
    const barW = spacing * 0.55;

    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          flex: 1,
          '@keyframes skeletonPulse': {
            '0%': { opacity: 0.6 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.6 },
          },
          animation: 'skeletonPulse 1.5s ease-in-out infinite',
        }}
      >
        <svg viewBox="0 0 480 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
          {/* Horizontal grid lines */}
          {[15, 35, 55].map((y, i) => (
            <line key={`g${i}`} x1="0" y1={y} x2="480" y2={y} stroke="currentColor" strokeOpacity="0.08" strokeWidth="0.5" />
          ))}
          {/* Zero line */}
          <line x1="0" y1={zeroY} x2="480" y2={zeroY} stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.5" />
          {/* Bars and connectors */}
          {bars.map((bar, i) => {
            const cx = spacing * i + spacing / 2;
            const x = cx - barW / 2;
            const yTop = toY(Math.max(bar.from, bar.to));
            const yBot = toY(Math.min(bar.from, bar.to));
            const h = yBot - yTop;
            const fillOpacity = bar.type === 'start' || bar.type === 'end' ? 0.22
              : bar.type === 'positive' ? 0.16 : 0.12;
            const showConnector = i < count - 1 && bars[i + 1].type !== 'end';
            const connectorY = toY(bar.to);
            const nextCx = spacing * (i + 1) + spacing / 2;
            return (
              <React.Fragment key={i}>
                <rect
                  x={x}
                  y={yTop}
                  width={barW}
                  height={Math.max(h, 1)}
                  rx="1.5"
                  fill="currentColor"
                  fillOpacity={fillOpacity}
                  stroke="currentColor"
                  strokeOpacity="0.2"
                  strokeWidth="0.3"
                />
                {showConnector && (
                  <line
                    x1={cx + barW / 2}
                    y1={connectorY}
                    x2={nextCx - barW / 2}
                    y2={connectorY}
                    stroke="currentColor"
                    strokeOpacity="0.15"
                    strokeWidth="0.5"
                    strokeDasharray="2 1"
                  />
                )}
              </React.Fragment>
            );
          })}
        </svg>
      </Box>
    );
  };

  // Box-and-whisker chart skeleton (like Nivo ResponsiveBoxPlot)
  const renderBoxPlotBody = () => {
    // Each entry: { whiskerTop, boxTop, median, boxBottom, whiskerBottom }
    // Values are in SVG y-coords (0 = top, 100 = bottom)
    const boxes = [
      { wT: 12, bT: 22, med: 32, bB: 48, wB: 62 },
      { wT: 8,  bT: 18, med: 28, bB: 42, wB: 55 },
      { wT: 15, bT: 28, med: 38, bB: 52, wB: 68 },
      { wT: 10, bT: 20, med: 30, bB: 44, wB: 58 },
      { wT: 18, bT: 30, med: 40, bB: 56, wB: 72 },
      { wT: 6,  bT: 16, med: 26, bB: 40, wB: 52 },
      { wT: 14, bT: 24, med: 34, bB: 50, wB: 65 },
      { wT: 20, bT: 32, med: 42, bB: 58, wB: 75 },
      { wT: 10, bT: 22, med: 32, bB: 46, wB: 60 },
      { wT: 16, bT: 26, med: 36, bB: 52, wB: 68 },
      { wT: 8,  bT: 18, med: 28, bB: 44, wB: 56 },
      { wT: 12, bT: 24, med: 34, bB: 48, wB: 62 },
      { wT: 14, bT: 26, med: 36, bB: 50, wB: 66 },
      { wT: 6,  bT: 14, med: 24, bB: 38, wB: 50 },
      { wT: 18, bT: 30, med: 42, bB: 54, wB: 70 },
      { wT: 10, bT: 22, med: 30, bB: 46, wB: 58 },
      { wT: 16, bT: 28, med: 38, bB: 52, wB: 66 },
      { wT: 8,  bT: 16, med: 26, bB: 42, wB: 54 },
      { wT: 20, bT: 34, med: 44, bB: 58, wB: 74 },
      { wT: 12, bT: 20, med: 30, bB: 44, wB: 56 },
      { wT: 14, bT: 26, med: 36, bB: 50, wB: 64 },
      { wT: 10, bT: 18, med: 28, bB: 40, wB: 52 },
      { wT: 16, bT: 28, med: 40, bB: 54, wB: 70 },
      { wT: 8,  bT: 20, med: 30, bB: 46, wB: 60 },
    ];

    const count = boxes.length;
    const spacing = 480 / count;
    const boxWidth = spacing * 0.4;

    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          flex: 1,
          '@keyframes skeletonPulse': {
            '0%': { opacity: 0.6 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.6 },
          },
          animation: 'skeletonPulse 1.5s ease-in-out infinite',
        }}
      >
        <svg
          viewBox="0 0 480 100"
          preserveAspectRatio="none"
          style={{ width: '100%', height: '100%' }}
        >
          {/* Horizontal grid lines */}
          {[20, 40, 60, 80].map((y, i) => (
            <line key={i} x1="0" y1={y} x2="480" y2={y} stroke="currentColor" strokeOpacity="0.1" strokeWidth="0.5" />
          ))}
          {/* Box plots */}
          {boxes.map((b, i) => {
            const cx = spacing * i + spacing / 2;
            const half = boxWidth / 2;
            return (
              <React.Fragment key={i}>
                {/* Whisker line */}
                <line x1={cx} y1={b.wT} x2={cx} y2={b.wB} stroke="currentColor" strokeOpacity="0.25" strokeWidth="1" />
                {/* Top whisker cap */}
                <line x1={cx - half * 0.5} y1={b.wT} x2={cx + half * 0.5} y2={b.wT} stroke="currentColor" strokeOpacity="0.25" strokeWidth="1" />
                {/* Bottom whisker cap */}
                <line x1={cx - half * 0.5} y1={b.wB} x2={cx + half * 0.5} y2={b.wB} stroke="currentColor" strokeOpacity="0.25" strokeWidth="1" />
                {/* Box (Q25-Q75) */}
                <rect
                  x={cx - half}
                  y={b.bT}
                  width={boxWidth}
                  height={b.bB - b.bT}
                  rx="1.5"
                  fill="currentColor"
                  fillOpacity="0.12"
                  stroke="currentColor"
                  strokeOpacity="0.25"
                  strokeWidth="0.8"
                />
                {/* Median line */}
                <line x1={cx - half} y1={b.med} x2={cx + half} y2={b.med} stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.2" />
              </React.Fragment>
            );
          })}
        </svg>
      </Box>
    );
  };

  // Vertical stacked bar chart skeleton (like JobsRoleMix)
  // Each bar = a company, segments stack bottom-to-top showing role percentages.
  const renderStackedBarBody = () => {
    const bars = [
      [0.35, 0.15, 0.38, 0.12], [0.28, 0.12, 0.45, 0.15],
      [0.32, 0.18, 0.35, 0.15], [0.40, 0.10, 0.32, 0.18],
      [0.25, 0.20, 0.40, 0.15], [0.30, 0.15, 0.35, 0.20],
      [0.38, 0.12, 0.30, 0.20], [0.22, 0.18, 0.42, 0.18],
      [0.36, 0.14, 0.36, 0.14], [0.28, 0.22, 0.32, 0.18],
      [0.33, 0.10, 0.40, 0.17], [0.42, 0.08, 0.30, 0.20],
      [0.26, 0.16, 0.44, 0.14], [0.34, 0.14, 0.34, 0.18],
      [0.30, 0.20, 0.30, 0.20],
    ];

    const count = bars.length;
    const chartTop = 5;
    const chartHeight = 88;
    const spacing = 480 / count;
    const barW = spacing * 0.62;
    const segOpacities = [0.22, 0.16, 0.12, 0.08];

    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          flex: 1,
          '@keyframes skeletonPulse': {
            '0%': { opacity: 0.6 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.6 },
          },
          animation: 'skeletonPulse 1.5s ease-in-out infinite',
        }}
      >
        <svg viewBox="0 0 480 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
          {/* Horizontal percentage grid lines */}
          {[0.25, 0.5, 0.75, 1.0].map((pct, i) => (
            <line key={`g${i}`} x1="0" y1={chartTop + chartHeight * (1 - pct)} x2="480" y2={chartTop + chartHeight * (1 - pct)} stroke="currentColor" strokeOpacity="0.08" strokeWidth="0.5" />
          ))}
          {/* Stacked vertical bars in sorted left-to-right order */}
          {bars.map((segs, i) => {
            const cx = spacing * i + spacing / 2;
            const x = cx - barW / 2;
            let yCursor = chartTop + chartHeight;
            return (
              <React.Fragment key={i}>
                {segs.map((seg, j) => {
                  const h = seg * chartHeight;
                  yCursor -= h;
                  const el = (
                    <rect key={j} x={x} y={yCursor} width={barW} height={Math.max(h, 0.4)} rx="1" fill="currentColor" fillOpacity={segOpacities[j]} stroke="currentColor" strokeOpacity="0.06" strokeWidth="0.3" />
                  );
                  return el;
                })}
              </React.Fragment>
            );
          })}
        </svg>
      </Box>
    );
  };

  const renderChartBody = () => {
    switch (variant) {
      case 'line':
        return renderLineChartBody();
      case 'scatter':
        return renderScatterChartBody();
      case 'treemap':
        return renderTreemapBody();
      case 'acceleration':
        return renderAccelerationBody();
      case 'boxplot':
        return renderBoxPlotBody();
      case 'waterfall':
        return renderWaterfallBody();
      case 'stackedBar':
        return renderStackedBarBody();
      case 'bar':
      default:
        return renderBarChartBody();
    }
  };

  // Treemap doesn't need axis labels
  if (variant === 'treemap') {
    return (
      <Box sx={{ width, height, ...(maxWidth && { maxWidth }), p: 2, ...sx }}>
        {renderTreemapBody()}
      </Box>
    );
  }

  return (
    <Box sx={{ width, height, display: 'flex', flexDirection: 'column', ...sx }}>
      {/* Chart area with y-axis */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {renderYAxisLabels()}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {renderChartBody()}
        </Box>
      </Box>
      {/* X-axis labels */}
      {variant === 'acceleration' ? renderAccelerationXAxisLabels() : renderXAxisLabels(variant === 'scatter' ? 5 : 6)}
    </Box>
  );
};

export default ChartSkeleton;
